import React, { useEffect, useState } from 'react';
import { SimpleGrid, useToast } from '@chakra-ui/react';
import { MdAgriculture, MdApartment, MdMap } from 'react-icons/md';
import { getDashboardCounts } from '../../../repositories/dashboard.repository';
import { listEquipmentTypes } from '../../../repositories/equipmentType.repository';
import BusinessUnitsPage from '../master-data/business-units';
import VillagesPage from '../master-data/villages';
import EquipmentsPage from '../master-data/equipments';
import {
  HubAmberButton,
  HubCard,
  HubError,
  HubGhostButton,
  HubLoading,
  HubPageHeader,
} from './hubUi';

/**
 * Master Data hub (ADMIN_PANEL_SCREENS.md §8) — Villages, Business Units & Sites,
 * Equipment types. Full Classic CRUD embedded inside Hub (Add / Edit / Map / etc.).
 */
export function HubMasterHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [equipmentTypeTotal, setEquipmentTypeTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [counts, types] = await Promise.all([
        getDashboardCounts(),
        listEquipmentTypes({ pageSize: 1 }).catch(() => null),
      ]);
      setData(counts);
      setEquipmentTypeTotal(types?.total != null ? Number(types.total) : null);
    } catch (e) {
      setError(e.message || 'Failed to load master data counts');
      toast({
        title: e.message || 'Failed to load master data counts',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const villages = data?.villages || {};
  const bus = data?.businessUnits || {};

  return (
    <>
      <HubPageHeader
        title="Master Data"
        subtitle="Villages, business units & sites, and equipment types — same features as Classic, inside Hub."
      />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <HubCard
          icon={MdMap}
          label="Villages"
          value={String(villages.total ?? 0)}
          hint={`${villages.active ?? 0} active · add / edit →`}
          onClick={() => push({ id: 'masterVillages', label: 'Villages' })}
        />
        <HubCard
          icon={MdApartment}
          label="Business units"
          value={String(bus.total ?? 0)}
          hint={`${bus.sites ?? 0} sites · add / edit →`}
          onClick={() =>
            push({ id: 'masterBusinessUnits', label: 'Business units' })
          }
        />
        <HubCard
          icon={MdAgriculture}
          label="Equipment types"
          value={equipmentTypeTotal != null ? String(equipmentTypeTotal) : '—'}
          hint="Types catalog · add / edit →"
          onClick={() =>
            push({ id: 'masterEquipmentTypes', label: 'Equipment types' })
          }
        />
      </SimpleGrid>
    </>
  );
}

export function HubMasterVillages() {
  return (
    <>
      <HubPageHeader
        title="Villages"
        subtitle="Master village list — add, edit, seed Mundra, map pin."
      />
      <VillagesPage embedded />
    </>
  );
}

export function HubMasterBusinessUnits({ push }) {
  return (
    <>
      <HubPageHeader
        title="Business units & sites"
        subtitle="Expand a BU to see sites. Add / edit BU or site, map preview — full Classic features in Hub."
        actions={
          <>
            <HubGhostButton
              onClick={() => push({ id: 'buReport', label: 'BU utilization' })}
            >
              Utilization report →
            </HubGhostButton>
            <HubAmberButton
              onClick={() => push({ id: 'masterHub', label: 'Master data' })}
            >
              All master data
            </HubAmberButton>
          </>
        }
      />
      <BusinessUnitsPage embedded />
    </>
  );
}

export function HubMasterEquipmentTypes() {
  return (
    <>
      <HubPageHeader
        title="Equipment types"
        subtitle="Equipment type catalog with required fields and documents (same as Classic)."
      />
      <EquipmentsPage embedded />
    </>
  );
}
